import { useMutation, useQueryClient } from '@tanstack/react-query'
import { App, Progress } from 'antd'
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import React from 'react'
import { contextCompanyIdSelector } from '../../../redux/context/company/selectors'
import { updateDriverData } from '../service/driverDataService'
import { FormulaMonthlyBudget } from '../types/driverData'
import { getDriverDataOptions } from './getDriverDataOptions'
import { apiPeriodGroupsSelector, filtersSelector } from '../../../redux/context/filters/selectors'
import { showSuccessMessage } from '../../../utils/message'
import { chunkArray } from '../../../utils/helpers'

const BATCH_SIZE = 100
const MAX_CONCURRENT_REQUESTS = 30

export const useUpdateDriverData = () => {
  const { t } = useTranslation()
  const companyId = useSelector(contextCompanyIdSelector)
  const { budgetingScenario } = useSelector(filtersSelector)
  const periodGroups = useSelector(apiPeriodGroupsSelector)
  const options = getDriverDataOptions(companyId!, budgetingScenario?.id, periodGroups)
  const { notification } = App.useApp()
  const queryClient = useQueryClient()

  if (!companyId) {
    throw new Error('Company ID is required for driver data.')
  }

  return useMutation({
    mutationKey: ['budget', 'driverData'],
    mutationFn: async (data: FormulaMonthlyBudget[]) => {
      // Small datasets: save directly without batching
      if (data.length <= BATCH_SIZE) {
        return updateDriverData(data, companyId, budgetingScenario?.id)
      }

      // Large datasets: split into batches and save with controlled concurrency
      // Note: Each batch is persisted independently. On failure, earlier batches remain saved.
      // Callers should expect partial saves and may need to handle accordingly.
      const batches = chunkArray(data, BATCH_SIZE)
      const queue = [...batches]
      const results: any[] = []
      let completedBatches = 0
      let hasError = false
      let errorMessage = ''

      // Show progress notification with progress bar
      const updateProgress = () => {
        const percent = Math.round((completedBatches / batches.length) * 100)
        const status = percent === 100 ? 'success' : 'active'
        notification.info({
          key: 'batch-save-progress',
          message: t('messages:savingData'),
          description: React.createElement(Progress, { percent, status }),
          duration: 0
        })
      }

      updateProgress()

      // Create worker pool for parallel processing
      const workers = Array.from({ length: MAX_CONCURRENT_REQUESTS }, () =>
        // eslint-disable-next-line no-loop-func
        (async () => {
          // eslint-disable-next-line no-await-in-loop
          while (queue.length > 0 && !hasError) {
            const batch = queue.shift()
            if (!batch) break

            try {
              // eslint-disable-next-line no-await-in-loop
              const result = await updateDriverData(batch, companyId, budgetingScenario?.id)

              if (Array.isArray(result)) {
                results.push(...result)
              }

              completedBatches += 1

              // Update progress bar
              updateProgress()
            } catch (error) {
              hasError = true
              errorMessage = error instanceof Error ? error.message : t('notifications:error:unknownError')
              break
            }
          }
        })()
      )

      await Promise.all(workers)

      notification.destroy('batch-save-progress')

      if (hasError) {
        throw new Error(
          `${t('messages:saveError')}: ${errorMessage} (${completedBatches}/${batches.length} batches saved)`
        )
      }

      return results
    },
    onSuccess() {
      // Invalidate driver data query
      queryClient.invalidateQueries({ queryKey: options.queryKey })
      // Note: Financial statements invalidation is handled by global mutation observer
      showSuccessMessage()
    },
    onError(error) {
      notification.error({ message: error.message })
    }
  })
}
